import React, { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  Row,
  Col,
  Button,
  Container,
  Card,
  Modal,
  Form,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaRocket,
  FaHandsHelping,
  FaLeaf,
  FaMapMarkerAlt,
  FaSuitcase,
} from "react-icons/fa";
import axios from "axios";
import Footer from "../Footer/Footer";
import Apiurl from "../environments/Apiurl"; // Import Apiurl for job openings API and career-enquiries (matches ERP)
import "../../assest/css/Career.css";

// Updated: Use Apiurl for consistency with ERP backend
const API_BASE = `${Apiurl}/career-enquiries`; // Points to ERP's /api/career-enquiries

function Career() {
  const [jobOpenings, setJobOpenings] = useState([]); // Dynamic state for job openings
  const [loadingJobs, setLoadingJobs] = useState(true); // Loading state for jobs
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    experience: "",
    domain: "",
    degree: "",
    message: "",
    resume: null,
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch job openings from API on component mount
  useEffect(() => {
    const fetchJobOpenings = async () => {
      try {
        setLoadingJobs(true);
        const response = await axios.get(`${Apiurl}/portfolio/job-openings`);
        setJobOpenings(response.data || []);
      } catch (error) {
        console.error("Error fetching job openings:", error);
        // Fallback to hardcoded if API fails (optional, for development)
        setJobOpenings([
          {
            _id: 1, // Use _id for consistency with MongoDB
            title: "UI/UX Designer",
            location: "Office / Salem",
            type: "Full-time",
            description:
              "Create engaging and user-friendly interfaces for web and mobile platforms. Collaborate with product and development teams to craft seamless user experiences.",
          },
          {
            _id: 2,
            title: "Software Developer",
            location: "Office / Salem",
            type: "Full-time",
            description:
              "Develop robust backend and frontend applications using modern technologies. Work on scalable solutions and contribute to innovative projects.",
          },
        ]);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobOpenings();
  }, []);

  const handleApplyClick = (job) => {
    // Pre-fill domain with job title
    setFormData(prev => ({
      ...prev,
      domain: job.title // Set default to job title
    }));
    setSelectedJob(job);
    setShowModal(true);
    setSubmitError("");
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFormData({ 
      full_name: "", 
      email: "", 
      phone_number: "", 
      experience: "",
      domain: "",
      degree: "",
      message: "", 
      resume: null 
    });
    setErrors({});
    setSubmitError("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone_number") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
        if (errors.phone_number) setErrors({ ...errors, phone_number: "" });
      }
    } else {
      setFormData({ ...formData, [name]: value });
      if (errors[name]) setErrors({ ...errors, [name]: "" });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/') && 
          file.type !== 'application/pdf' &&
          file.type !== 'application/msword' &&
          file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        setErrors({
          ...errors,
          resume: 'Please select an image, PDF, DOC, or DOCX file.',
        });
        e.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors({
          ...errors,
          resume: 'File size must be less than 5MB.',
        });
        e.target.value = '';
        return;
      }
      setErrors({ ...errors, resume: "" });
      setFormData({ ...formData, resume: file });
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Please enter a valid email address.";
    if (!formData.phone_number.trim()) newErrors.phone_number = "Phone number is required.";
    else if (formData.phone_number.length !== 10 || !/^\d{10}$/.test(formData.phone_number)) newErrors.phone_number = "Phone number must be exactly 10 digits.";
    if (!formData.resume) newErrors.resume = "Please upload your resume.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.full_name);
      submitData.append('email', formData.email);
      submitData.append('phone_number', formData.phone_number);
      submitData.append('experience', formData.experience);
      submitData.append('domain', formData.domain);
      submitData.append('degree', formData.degree);
      submitData.append('message', formData.message);
      submitData.append('resume', formData.resume);
      // Optionally append job title if schema updated (add 'position' field to model if needed)
      if (selectedJob) {
        submitData.append('position', selectedJob.title); // Note: Add this field to schema if you want to store applied position
      }

      const response = await axios.post(API_BASE, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log("✅ Application submitted:", response.data); // Logs saved enquiry
      alert("Application submitted successfully! We'll review it soon.");
      handleModalClose();
    } catch (err) {
      console.error("❌ Submit error:", err);
      setSubmitError(err.response?.data?.message || "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <HelmetProvider>
      <div className="container-fluid p-0 about-container">
        <Helmet>
          <title>Careers - Aalan TechSoft | Join Our Team</title>
          <meta
            name="description"
            content="Explore career opportunities at Aalan TechSoft. Be part of a team that innovates, builds, and inspires the next generation of technology solutions."
          />
        </Helmet>

        {/* Hero Section */}
        <div className="Portfolio-hero">
          <div className="about-content text-center">
            <div className="hero-title-container">
              <span className="badge-title">Careers</span>
              <h1 className="text-white fw-bold">
                Build Your Future with Aalan TechSoft
              </h1>
            </div>
          </div>
        </div>

        {/* Job Openings Section */}
        <section className="career-openings py-5">
          <Container>
            <Row className="text-center mb-5">
              <h2 className="section-title">Current <span style={{ color: "#85bf39" }}>Openings</span></h2>
              <p className="section-text text-center">
                Explore our available roles and find the one that fits you best.
              </p>
            </Row>

            {loadingJobs ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="success" />
                <p className="mt-2">Loading job openings...</p>
              </div>
            ) : jobOpenings.length > 0 ? (
              <Row className="g-4">
                {jobOpenings.map((job) => (
                  <Col md={6} key={job._id || job.id}>
                    <Card className="job-card border-0 shadow-sm h-100 p-3">
                      <Card.Body>
                        <h5 className="fw-semibold">{job.title}</h5>
                        <p className="text-muted mb-2 d-flex align-items-center">
                          <FaMapMarkerAlt
                            className="me-2"
                            style={{ color: "#85bf39" }}
                          />
                          <span>{job.location}</span>
                        </p>

                        <Badge bg="info">{job.type}</Badge>
                        <p className="Section-text">{job.description}</p>
                        <Button
                          className="apply-btn mt-3"
                          onClick={() => handleApplyClick(job)}
                        >
                          <FaSuitcase className="me-2" />
                          Apply Now
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5">
                <FaLeaf size={50} color="#85bf39" className="mb-3" />
                <h5 className="fw-semibold">No Current Openings</h5>
                <p className="text-muted">
                  We don’t have any active vacancies right now. <br />
                  Stay tuned or check back later for future opportunities!
                </p>
              </div>
            )}
          </Container>
        </section>

        {/* Why Work With Us */}
        <section className="career-culture py-5 bg-light">
          <Container>
            <Row className="text-center mb-5">
              <h2 className="section-title">Why Work <span style={{ color: "#85bf39" }}>With Us?</span></h2>
              <p className="section-text text-center">
                Experience a culture that values creativity, growth, and balance.
              </p>
            </Row>
            <Row className="g-4 text-center">
              <Col md={4}>
                <div className="culture-card shadow-sm p-4 rounded h-100">
                  <FaRocket size={40} color="#85bf39" className="mb-3" />
                  <h5 className="fw-bold mb-3">Growth Opportunities</h5>
                  <p>
                    We invest in your learning and give you the freedom to
                    explore, innovate, and grow.
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div className="culture-card shadow-sm p-4 rounded h-100">
                  <FaHandsHelping size={40} color="#85bf39" className="mb-3" />
                  <h5 className="fw-bold mb-3">Collaborative Culture</h5>
                  <p>
                    Work in a team that celebrates success together and
                    encourages new ideas every day.
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div className="culture-card shadow-sm p-4 rounded h-100">
                  <FaLeaf size={40} color="#85bf39" className="mb-3" />
                  <h5 className="fw-bold mb-3">Work-Life Balance</h5>
                  <p>
                    Enjoy flexible working hours and a healthy balance between
                    your career and personal life.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        

        {/* Apply Modal */}
        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Apply for {selectedJob?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.full_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.full_name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  isInvalid={!!errors.phone_number}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone_number}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Experience</Form.Label>
                <Form.Control
                  type="text"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Domain/Field of Interest</Form.Label>
                <Form.Control
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Degree/Education</Form.Label>
                <Form.Control
                  type="text"
                  name="degree"
                  value={formData.degree}
                  onChange={handleInputChange}
                  placeholder="e.g., B.Tech in Computer Science"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Resume</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                  isInvalid={!!errors.resume}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.resume}
                </Form.Control.Feedback>
                {formData.resume && (
                  <small className="text-success mt-2 d-block">
                    ✅ Uploaded: {formData.resume.name}
                  </small>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Message (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </Form.Group>

              {submitError && <Alert variant="danger">{submitError}</Alert>}

              <div className="text-center">
                <Button type="submit" className="apply-btn" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        <Footer />
      </div>
    </HelmetProvider>
  );
}

export default Career;